import React, { useState, useEffect, useRef } from 'react';
import { userFeedbackManager, buildMailtoUrl, type UserFeedback } from '../../utils/userFeedback';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialType?: UserFeedback['type'];
  initialCategory?: string;
  initialError?: string;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  isOpen,
  onClose,
  initialType = 'general_feedback',
  initialCategory = 'general',
  initialError,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    type: initialType,
    title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
    description: initialError || '',
    category: initialCategory,
    priority: 'medium' as UserFeedback['priority'],
  });

  const feedbackTypes = [
    { value: 'bug_report',        label: 'Bug Report' },
    { value: 'feature_request',   label: 'Feature Request' },
    { value: 'general_feedback',  label: 'General Feedback' },
    { value: 'usability_issue',   label: 'Usability Issue' },
    { value: 'performance_issue', label: 'Performance Issue' },
  ];

  const categories = ['general', 'color_tools', 'performance', 'bug', 'feature_request', 'accessibility', 'palette', 'print'];

  const priorities = [
    { value: 'low',      label: 'Low' },
    { value: 'medium',   label: 'Medium' },
    { value: 'high',     label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  // Hooks before any early return
  const contentRef = useRef<HTMLDivElement>(null);

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: initialType,
        title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
        description: initialError || '',
        category: initialCategory,
        priority: 'medium',
      });
      setSubmitted(false);
      setFeedbackId(null);
    }
  }, [isOpen, initialType, initialCategory, initialError]);

  // Simple focus management
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...formData, url: window.location.href };
      const id = userFeedbackManager.submitFeedback(payload);
      setFeedbackId(id);

      const mailtoUrl = buildMailtoUrl(payload);
      if (mailtoUrl) window.location.href = mailtoUrl;

      setSubmitted(true);
    } catch (err) {
      console.error('Feedback submission failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        ref={contentRef}
        tabIndex={-1}
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto outline-none"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Send Feedback</h2>
            <button onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {submitted ? (
            /* Success state */
            <div className="text-center py-8">
              <p className="text-lg font-medium mb-2">Thank you!</p>
              <p className="text-sm text-gray-500 mb-4">Your mail client should have opened. Send the pre-filled email to complete.</p>
              {feedbackId && <p className="text-xs text-gray-400 mb-4">Ref: {feedbackId}</p>}
              <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Type */}
              <div>
                <label htmlFor="fb-type" className="block text-sm font-medium mb-1">
                  Type
                </label>
                <select
                  id="fb-type"
                  value={formData.type}
                  onChange={e => handleChange('type', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800"
                >
                  {feedbackTypes.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                </select>
              </div>

              {/* Category */}
              <div>
                <label htmlFor="fb-category" className="block text-sm font-medium mb-1">
                  Category
                </label>
                <select
                  id="fb-category"
                  value={formData.category}
                  onChange={e => handleChange('category', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800"
                >
                  {categories.map(c => (
                    <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div>
                <p className="block text-sm font-medium mb-1">Priority</p>
                <div className="flex gap-2">
                  {priorities.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => handleChange('priority', p.value)}
                      className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${
                        formData.priority === p.value
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label htmlFor="fb-title" className="block text-sm font-medium mb-1">
                  Title *
                </label>
                <input
                  id="fb-title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={e => handleChange('title', e.target.value)}
                  placeholder="Brief summary"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="fb-description" className="block text-sm font-medium mb-1">
                  Description *
                </label>
                <textarea
                  id="fb-description"
                  rows={5}
                  required
                  value={formData.description}
                  onChange={e => handleChange('description', e.target.value)}
                  placeholder="Please describe in detail..."
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isSubmitting ? 'Opening mail…' : 'Send Feedback'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Floating trigger button ----------

interface FeedbackButtonProps {
  onOpen: (type?: UserFeedback['type'], category?: string, error?: string) => void;
}

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({ onOpen }) => (
  <button
    id="feedback-trigger-btn"
    onClick={() => onOpen()}
    className="fixed bottom-4 right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-40"
    aria-label="Send feedback"
    title="Send feedback"
  >
    {/* Chat bubble icon */}
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  </button>
);
